import { Description, Dialog as HDialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from './IconButton';

export interface OverlayProps {
    open: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    /** Sticky footer with the actions. */
    footer?: ReactNode;
    /** A submit is in flight: Esc, scrim click and the close button do nothing. */
    busy?: boolean;
    children?: ReactNode;
}

interface InternalProps extends OverlayProps {
    variant: 'dialog' | 'drawer';
    size?: 'md' | 'lg';
}

const POSITION = {
    dialog: 'items-end justify-center sm:items-center sm:p-4',
    drawer: 'items-end justify-center sm:items-stretch sm:justify-end',
};

const PANEL = {
    dialog: clsx(
        'rounded-t-sheet sm:rounded-card',
        'data-[closed]:translate-y-full data-[closed]:opacity-0 sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-[0.98]'
    ),
    drawer: clsx(
        'rounded-t-sheet sm:w-[30rem] sm:max-w-full sm:rounded-none sm:shadow-[inset_1px_0_0_var(--border-hairline)]',
        'data-[closed]:translate-y-full data-[closed]:opacity-0 sm:data-[closed]:translate-x-full sm:data-[closed]:translate-y-0 sm:data-[closed]:opacity-100'
    ),
};

/** Shared by Dialog and Drawer: a centred dialog or right drawer from 640 px, a bottom sheet below. */
export const Overlay = ({ open, onClose, title, description, footer, busy, children, variant, size = 'md' }: InternalProps) => {
    const { t } = useTranslation();
    const close = () => {
        if (!busy) onClose();
    };
    return (
        <HDialog open={open} onClose={close} className="relative z-50">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-scrim transition-opacity duration-fade ease-snap data-[closed]:opacity-0"
            />
            <div className={clsx('fixed inset-0 flex', POSITION[variant])}>
                <DialogPanel
                    transition
                    className={clsx(
                        'flex max-h-[92dvh] w-full flex-col bg-raised shadow-e3 transition duration-fade ease-snap',
                        variant === 'dialog' && (size === 'lg' ? 'sm:max-w-[35rem]' : 'sm:max-w-md'),
                        variant === 'drawer' && 'sm:max-h-none',
                        PANEL[variant]
                    )}
                >
                    <div aria-hidden="true" className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-chip bg-control sm:hidden" />
                    <div className="flex items-start justify-between gap-4 px-4 pb-2 pt-4 sm:p-6 sm:pb-2">
                        <div className="min-w-0">
                            <DialogTitle as="h2" className="text-title text-strong">
                                {title}
                            </DialogTitle>
                            {description && (
                                <Description className="mt-1 text-small text-muted">{description}</Description>
                            )}
                        </div>
                        <IconButton aria-label={t('ui.close')} onClick={close} disabled={busy} className="-mr-2 -mt-1 shrink-0 sm:-mr-3">
                            <XMarkIcon className="size-6" aria-hidden="true" />
                        </IconButton>
                    </div>
                    {children && <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2 sm:px-6">{children}</div>}
                    {footer && (
                        <div className="flex flex-col-reverse gap-2 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 sm:flex-row sm:justify-end sm:p-6 sm:pt-4">
                            {footer}
                        </div>
                    )}
                </DialogPanel>
            </div>
        </HDialog>
    );
};
